import { RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';
import Button from '../../../components/Button';
import { ButtonWithConfirmation } from '../../../components/ButtonWithConfirmation';
import ErrorDisplay from '../../../components/ErrorDisplay';
import { formatDateTime } from '../../../utils/timeUtils';
import type { OcdCycleStepsData } from '../services/ocdCycleStepsTypes';
import { userProfileService } from '../services/userProfileService';

interface OcdCycleStepsCardProps {
    userId: string;
}

/** The user's OCD cycle steps, with a button that generates them again. */
export const OcdCycleStepsCard = ({ userId }: OcdCycleStepsCardProps) => {
    const [ocdCycleSteps, setOcdCycleSteps] = useState<OcdCycleStepsData | null>(null);
    const [isLoadingCycleSteps, setIsLoadingCycleSteps] = useState(false);
    const [isRegeneratingCycleSteps, setIsRegeneratingCycleSteps] = useState(false);
    const [cycleStepsError, setCycleStepsError] = useState<string | null>(null);
    const [cycleStepsSuccessMessage, setCycleStepsSuccessMessage] = useState<string | null>(null);

    useEffect(() => {
        const loadOcdCycleSteps = async () => {
            setIsLoadingCycleSteps(true);
            setCycleStepsError(null);
            setCycleStepsSuccessMessage(null);

            try {
                const steps = await userProfileService.getOcdCycleSteps(userId);
                setOcdCycleSteps(steps);
            } catch (err) {
                setCycleStepsError(err instanceof Error ? err.message : 'Failed to load OCD cycle steps');
                setOcdCycleSteps(null);
            } finally {
                setIsLoadingCycleSteps(false);
            }
        };

        loadOcdCycleSteps();
    }, [userId]);

    const handleRegenerateCycleSteps = async () => {
        setIsRegeneratingCycleSteps(true);
        setCycleStepsError(null);
        setCycleStepsSuccessMessage(null);

        try {
            await userProfileService.regenerateOcdCycleSteps(userId);
            const updatedSteps = await userProfileService.getOcdCycleSteps(userId);
            setOcdCycleSteps(updatedSteps);
            setCycleStepsSuccessMessage('OCD cycle steps regenerated successfully!');
        } catch (err) {
            setCycleStepsError(err instanceof Error ? err.message : 'Failed to regenerate OCD cycle steps');
        } finally {
            setIsRegeneratingCycleSteps(false);
        }
    };

    return (
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                <h2 className="text-xl font-semibold text-gray-900">OCD Cycle Steps</h2>
                {isLoadingCycleSteps || isRegeneratingCycleSteps ? (
                    <Button
                        disabled
                        variant="primary"
                        size="sm"
                        icon={<RefreshCw className={`w-4 h-4 ${isRegeneratingCycleSteps ? 'animate-spin' : ''}`} />}
                    >
                        {isRegeneratingCycleSteps ? 'Regenerating...' : 'Regenerate'}
                    </Button>
                ) : (
                    <ButtonWithConfirmation
                        onClick={handleRegenerateCycleSteps}
                        firstText="Regenerate"
                        secondText="Confirm Regenerate"
                        firstIcon={<RefreshCw className="w-4 h-4" />}
                        secondIcon={<RefreshCw className="w-4 h-4" />}
                        firstVariant="primary"
                        secondVariant="danger"
                        size="sm"
                    />
                )}
            </div>

            {cycleStepsError && <ErrorDisplay message={cycleStepsError} />}
            {cycleStepsSuccessMessage && (
                <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
                    {cycleStepsSuccessMessage}
                </div>
            )}

            {isLoadingCycleSteps ? (
                <p className="text-sm text-gray-600">Loading OCD cycle steps...</p>
            ) : isRegeneratingCycleSteps ? (
                <p className="text-sm text-gray-600">Regenerating OCD cycle steps. This may take a minute...</p>
            ) : ocdCycleSteps ? (
                <div className="space-y-4">
                    <div className="text-sm text-gray-800 space-y-1">
                        <p>
                            Created At: <span className="font-bold">{formatDateTime(ocdCycleSteps.createdAt)}</span>
                        </p>
                        <p>
                            Updated At: <span className="font-bold">{formatDateTime(ocdCycleSteps.updatedAt)}</span>
                        </p>
                        <p>
                            Step Count: <span className="font-bold">{ocdCycleSteps.steps.length}</span>
                        </p>
                    </div>

                    <div className="space-y-3">
                        {ocdCycleSteps.steps
                            .slice()
                            .sort((a, b) => a.cycleStep - b.cycleStep)
                            .map((step) => (
                                <div
                                    key={step.cycleStep}
                                    className="border border-gray-200 rounded-md p-3 bg-gray-50"
                                >
                                    <p className="text-xs font-semibold text-gray-500 mb-1">
                                        Step {step.cycleStep}
                                    </p>
                                    <p className="text-sm text-gray-800">{step.ollieText}</p>
                                </div>
                            ))}
                    </div>
                </div>
            ) : (
                <p className="text-sm text-gray-600">No OCD cycle steps generated yet.</p>
            )}
        </div>
    );
};
